import { config } from "../utils/config"
import { logSentry } from "../utils/sentry";
import { setPaymentMethod, setDisabledItem, startLoading, stopLoading, setShowWarningOnCartPage } from "./application";
import { set as setCookie, get as getCookie, remove as removeCookie} from "../utils/cookies";
import { redirectToURL } from "../utils/url";
import { formatPhoneNumber } from "../utils/payment";
import abcService from "../services/abc"
import { customerApi } from "./customer";
import { customerGdprAPI } from "./customerGdpr";
import { cartApi } from "./cart";
import { getCart } from "./newCart";
import { brainTreeApi, brainTreeApiV2ApplePay, brainTreeApiV2Card } from "./braintree";
import { dispatchGaAndFacebookEvent } from "../utils/analytics";
import { discountApi } from "./discount";
import brainTreeClient from "braintree-web/client"
import paypalCheckoutClient from "braintree-web/paypal-checkout" 
import Cart from "../models/cart";
import { onProceedToCheckoutOperation } from "../components/shopping-bag/ga-module";
import i18n from '../utils/translations/i18n';
import { get as getFromLocalStorage, set as setInLocalStorage } from '../utils/storage/localStorage';
import customerActions from "./customerActions";
import { newCartApi } from "./newCart";
import { Address } from "../enums/checkoutShipping";
import { addPCCFlag } from "../components/shopping-bag/ga-module";
import { CHANNEL_TYPE, EXPRESS_CHECKOUT_COOKIE_NAMES, PAYMENT_METHODS, PAYMENT_METHOD_CAMEL_CASE } from "../enums/checkoutPayments";

declare const paypal: any;
declare const window: any;
let getCustomerResponse: boolean = false;

const createClientInstance = async (paypalTokenResponse) => {
    return brainTreeClient.create({
        authorization: decodeURIComponent(paypalTokenResponse)
    });
}

const loadPaypalSDK = async (paypalTokenResponse, loadPaypalOptions) => {
    return createClientInstance(paypalTokenResponse).then(clientInstance => {
        return paypalCheckoutClient.create({
            client: clientInstance
        })
    }).then(paypalCheckoutInstance => {
        return paypalCheckoutInstance.loadPayPalSDK(loadPaypalOptions)
    })
}

let buttons;
let hasRendered = false;

const getPayPalSDKConfig = (currency: string, isPayLater: boolean) => {
    const paypalLocale = getPaypalSupportedLocale();
    return {
        components: 'buttons,messages',
        currency,
        intent: 'capture',
        locale: paypalLocale,
        ...((isPayLater && !(config('regionCode') === 'DE')) ? {'enable-funding': 'paylater'} : {'disable-funding': 'paylater'}),
    };
};

const paypalPayLaterIsEligible = async (dispatch, cartId, isPayLater) => {
    const { data: { token, currency } } = await dispatch(brainTreeApi.endpoints.getPaypalAuthToken.initiate({ cartId: cartId, paymentMethod: PAYMENT_METHOD_CAMEL_CASE.PAYPAL }));
    try {
        await loadPaypalSDK(token, getPayPalSDKConfig(currency, isPayLater));
        return paypal?.Buttons({
            ...((isPayLater && !(config('regionCode') === 'DE')) ? {fundingSource: paypal.FUNDING.PAYLATER}:{}),
        }).isEligible();
    } catch (error) {
        throw error;
    }
}

const intantiatePayPalInstance = (paypalTokenResponse, dataCurrency, cartSubtotal, setShowPaymentError, dispatch, cartId, redirectToOrderConfirmation, cart, coupon, isPayLater) => {
    loadPaypalSDK(paypalTokenResponse, getPayPalSDKConfig(dataCurrency, isPayLater)).then(paypalCheckoutInstance => {
        if (buttons && buttons.close && hasRendered) {  
            buttons.close();
            hasRendered = false;
        }
        buttons = paypal.Buttons({
            ...((isPayLater && !(config('regionCode') === 'DE')) ? {fundingSource: paypal.FUNDING.PAYLATER}:{}),
            createOrder: function () {
                (async function() {
                    if (coupon?.code) await dispatch(discountApi.endpoints.validateCoupon.initiate({ couponCode: coupon?.code , 'locale': config('locale') , callApplyCoupon: false}));
                })();
                var _metaData = {
                    'event': "payment_method",
                    'payment_type': isPayLater ? 'Paypal Paylater' :'Paypal',
                    'step_name': 'continue_button', // based on type of payment
                    'step_no': 1,
                    ...addPCCFlag(cart?.hasPCCItem)
                }
                dispatchGaAndFacebookEvent(_metaData);
                return paypalCheckoutInstance.createPayment({
                    flow: 'checkout',
                    amount: cartSubtotal,
                    currency: dataCurrency, // Make sure this matches the currency passed into loadPayPalSDK
                    intent: 'sale' // Make sure this matches the intent passed into loadPayPalSDK
                });
            },
            onClick : function(){
                setShowPaymentError(false);
            },
            onCancel : function(){
                var _metaData = {
                    'event': "payment_method",
                    'payment_type': isPayLater ? 'Paypal Paylater' :'Paypal',
                    'step_name': 'cancel_payment',
                    'step_no': 0,
                    ...addPCCFlag(cart?.hasPCCItem)
                }
                dispatchGaAndFacebookEvent(_metaData);
            },
            onError: function (err) {
                var _metaData = {
                    'event': "payment_method",
                    'payment_type': isPayLater ? 'Paypal Paylater' :'Paypal',
                    'step_name': 'payment_fail', // based on type of payment
                    'step_no': 2,
                    ...addPCCFlag(cart?.hasPCCItem)
                }
                dispatchGaAndFacebookEvent(_metaData);
                showPaypalErrorMessage(setShowPaymentError);
            },

            onApprove: function (data) {
                return paypalCheckoutInstance.tokenizePayment(data)
                    .then(async function (payload) {
                        // Submit `payload.nonce` to your server.
                        dispatch(setPaymentMethod(isPayLater ? 'paypal_pay_later':'paypal'));
                        submitNonce(dispatch, cartId, redirectToOrderConfirmation, payload.nonce, setShowPaymentError, isPayLater ? 'Paypal Paylater' :'Paypal', cart)
                    });
            },    
            style: {
                color:  'gold',
                shape:  'pill',
                label:  '',
                height: 40,
                size: 'responsive',
                layout: 'horizontal',
                tagline:false
            }
        });
        const paypalBtn = document.getElementById('paypal-button');
        const paypalBtnSkeleton = paypalBtn && paypalBtn.querySelector('.paypal-skeleton');
        if (paypalBtnSkeleton) paypalBtnSkeleton.remove();

        paypalBtn && buttons.render('#paypal-button').then(function () {
            hasRendered = true;
        }).catch((err) => {
            let selector = document.querySelector('#paypal-button');
            // button failed to render, possibly because it was closed or destroyed.
            if (selector && selector.children.length > 0) {
                // still mounted so throw an error
                throw new Error(err); 
            }
        })
    })
}

const intantiatePayPalInstanceNew = (paypalTokenResponse, dataCurrency, cartSubtotal, setShowPaymentError, dispatch, cartId, redirectToOrderConfirmation, cart, coupon, isPayLater) => {
    loadPaypalSDK(paypalTokenResponse, getPayPalSDKConfig(dataCurrency, isPayLater)).then(paypalCheckoutInstance => {
        if (buttons && buttons.close && hasRendered) {  
            buttons.close();
            hasRendered = false;
        }
        buttons = paypal.Buttons({
            ...((isPayLater && !(config('regionCode') === 'DE')) ? {fundingSource: paypal.FUNDING.PAYLATER}:{}),
            createOrder: function () {
                (async function() {
                    if (coupon?.code) await dispatch(discountApi.endpoints.validateCoupon.initiate({ couponCode: coupon?.code , 'locale': config('locale') , callApplyCoupon: false}));
                })();
                var _metaData = {
                    'event': "payment_method",
                    'payment_type': isPayLater ? 'Paypal Paylater' :'Paypal',
                    'step_name': 'continue_button', // based on type of payment
                    'step_no': 1,
                    ...addPCCFlag(cart?.hasPCCItem)
                }
                dispatchGaAndFacebookEvent(_metaData);
                return paypalCheckoutInstance.createPayment({
                    flow: 'checkout',
                    amount: cartSubtotal,
                    currency: dataCurrency, // Make sure this matches the currency passed into loadPayPalSDK
                    intent: 'sale' // Make sure this matches the intent passed into loadPayPalSDK
                });
            },
            onClick : function(){
                setShowPaymentError(false);
            },
            onCancel : function(){
                var _metaData = {
                    'event': "payment_method",
                    'payment_type': isPayLater ? 'Paypal Paylater' :'Paypal',
                    'step_name': 'cancel_payment',
                    'step_no': 0,
                    ...addPCCFlag(cart?.hasPCCItem)
                }
                dispatchGaAndFacebookEvent(_metaData);
            },
            onError: function (err) {
                var _metaData = {
                    'event': "payment_method",
                    'payment_type': isPayLater ? 'Paypal Paylater' :'Paypal',
                    'step_name': 'payment_fail', // based on type of payment
                    'step_no': 2,
                    ...addPCCFlag(cart?.hasPCCItem)
                }
                dispatchGaAndFacebookEvent(_metaData);
                showPaypalErrorMessage(setShowPaymentError);
            },

            onApprove: function (data) {
                return paypalCheckoutInstance.tokenizePayment(data)
                    .then(async function (payload) {
                        // Submit `payload.nonce` to your server.
                        dispatch(setPaymentMethod(isPayLater ? 'paypal_pay_later':'paypal'));
                        submitNonce(dispatch, cartId, redirectToOrderConfirmation, payload.nonce, setShowPaymentError, isPayLater ? 'Paypal Paylater' :'Paypal', cart)
                    });
            },
            style: {
                color:  'gold',
                shape:  'pill',
                label:  '',
                height: 55,
                size: 'responsive',
                layout: 'horizontal',
                tagline:false,
                disableMaxWidth: true
            }
        });

        const id = isPayLater ? '#paypal-button' : '#paypal-button2';
        (document.getElementById('paypal-button') || document.getElementById('paypal-button2')) && buttons.render(id).then(function () {
            hasRendered = true;
        }).catch((err) => {
            let selector = document.querySelector(id);
            // button failed to render, possibly because it was closed or destroyed.
            if (selector && selector.children.length > 0) {
                // still mounted so throw an error
                throw new Error(err); 
            }
        })
    })
}

const loadPayPalInstance = (cartId: string, user: any ,cartSubtotal: number, shippingType: string, setShowPaymentError: any, redirectToOrderConfirmation: any, isPayLater: boolean, paypalNew: boolean = false) => {
    return async (dispatch, getState) => {
        try {
            const {applicationState: {user, coupon}} = getState();
            const cart = getCart(getState(), {cartId: user.cartId, userId: user.userId, isLoggedIn: user.isLoggedIn})?.data;

            const { data: { token, currency } } = await dispatch(brainTreeApi.endpoints.getPaypalAuthToken.initiate({ cartId: user.cartId, paymentMethod: PAYMENT_METHOD_CAMEL_CASE.PAYPAL }));
            if(token) {
                if (paypalNew) {
                    intantiatePayPalInstanceNew(token, currency, cartSubtotal, setShowPaymentError, dispatch, cartId, redirectToOrderConfirmation , cart, coupon, isPayLater)
                } else {
                    //remove this after total migration
                    intantiatePayPalInstance(token, currency, cartSubtotal, setShowPaymentError, dispatch, cartId, redirectToOrderConfirmation , cart, coupon, isPayLater)
                }
            }
        } catch (error) {
            const {applicationState: {currentPage}} = getState();
            logSentry({
                'page': currentPage,
                'feature': isPayLater ? 'paypalPayLater' : 'paypal',
                'operation': 'paypal checkout',
                'errorType': 'ajaxError',
                'err': JSON.stringify(error),
                'func': 'loadPayPalInstance',
                'errMsg': 'Error:  while performing paypal checkout'
            })
        }
    } 
}

const loadPaypal = (cartId: string, user: any, shippingType: string, setShowPaymentError: any, cart: Cart, navigate: any, triggerMiniCartQuery: any) => {
    return async (dispatch, getState) => {
        try {
            removeCookie('expressCheckout');
            const {applicationState: {currentPage, disabledItem, showWarning,coupon,isShippingDisc}} = getState();

            const { data: { token, currency } } = await dispatch(brainTreeApi.endpoints.getPaypalAuthToken.initiate({ cartId: user.cartId, paymentMethod: PAYMENT_METHOD_CAMEL_CASE.PAYPAL }));
            const paypalLocale = getPaypalSupportedLocale();
            if (token) {
                loadPaypalSDK(token, {
                    components: 'buttons,messages',
                    currency: currency,
                    intent: 'authorize',
                    locale: paypalLocale,
                    commit: false,
                    'disable-funding': 'paylater,card'
                } ).then(paypalCheckoutInstance => {
                    const paypalBtn = document.getElementById('paypal-button');
                    const paypalBtnSkeleton = paypalBtn && paypalBtn.querySelector('.paypal-skeleton');
                    if (paypalBtnSkeleton) paypalBtnSkeleton.remove();

                    return paypalBtn && paypal.Buttons({
                        createOrder: function () {
                            const cartData = getCart(getState(), {cartId: user.cartId, userId: user.userId, isLoggedIn: user.isLoggedIn})?.data;
                            const cartSubtotal: number = Number((cartData.cartSubtotal / Math.pow(10, cartData.fractionDigits)).toFixed(cartData.fractionDigits));
                            const _metaData = {
                                'event': 'payment_method',
                                'payment_type': 'Paypal Checkout',
                                'step_name': 'continue_button',
                                'step_no': 1,
                                ...addPCCFlag(cart?.hasPCCItem)
                            }
                            dispatchGaAndFacebookEvent(_metaData);
                            onProceedToCheckoutOperation(cartData, currentPage,coupon,isShippingDisc, true);
                            return paypalCheckoutInstance.createPayment({
                                flow: 'checkout',
                                amount: cartSubtotal,
                                currency: currency, // Make sure this matches the currency passed into loadPayPalSDK
                                intent: 'authorize',
                                enableShippingAddress: true,
                                enableBillingAddress: true,
                                shippingAddressEditable: true
                            });
                        },
                        onClick : function(){
                            setShowPaymentError(false);
                        },
                        onCancel : function(){
                            var _metaData = {
                                'event': "payment_method",
                                'payment_type': 'Paypal Checkout',
                                'step_name': 'cancel_payment',
                                'step_no': 0,
                                ...addPCCFlag(cart?.hasPCCItem)
                            }
                            dispatchGaAndFacebookEvent(_metaData);
                        },
                        onError: function (err) {
                            var _metaData = {
                                'event': "payment_method",
                                'payment_type': 'Paypal Checkout',
                                'step_name': 'payment_fail', // based on type of payment
                                'step_no': 2,
                                ...addPCCFlag(cart?.hasPCCItem)
                            }
                            dispatchGaAndFacebookEvent(_metaData);
                            window.np.errorTrackingGaEvent('cart', err);
                            showPaypalErrorMessage(setShowPaymentError);
                        },
                        onApprove: function (data) {
                            return paypalCheckoutInstance.tokenizePayment(data)
                                .then(async function (payload) {
                                    // Submit `payload.nonce` to your server.
                                    try{
                                        dispatch(startLoading());
                                        if(disabledItem) {
                                            dispatch(setDisabledItem(false));
                                        }
                                        if(showWarning){
                                            dispatch(setShowWarningOnCartPage(false));
                                        }
                                        const nonce = payload.nonce;
                                        const expressCheckoutCookieName = EXPRESS_CHECKOUT_COOKIE_NAMES.EXPRESS_CHECKOUT
                                        let {customerData,shippingAddressData,billingAddressData} = createDataFromPaypalDetails(payload.details);
                                        if (shippingAddressData.country !== window.locale_details.country_code) throw { message:'Country mismatch while authorizing in paypal express flow' } //eslint-disable-line
                                        if (user.isLoggedIn === 'true') {
                                            updateCustomerAndCartAddressLoggedIn({shippingAddressData,billingAddressData,nonce, expressCheckoutCookieName, user, cartId, shippingType,dispatch,navigate, triggerMiniCartQuery, channelType: CHANNEL_TYPE.PAYPAL_EXPRESS});
                                        }
                                        else {
                                            updateCustomerAndCartAddressGuest({customerData,shippingAddressData,billingAddressData,nonce, expressCheckoutCookieName, cartId, user, shippingType, dispatch, setShowPaymentError,navigate, triggerMiniCartQuery});
                                        }
                                     }
                                     catch(error: any) {
                                         showPaypalErrorMessage(setShowPaymentError);
                                         dispatch(stopLoading());
                                         logSentry({
                                            'page': currentPage,
                                            'feature': 'paypalExpressCheckout',
                                            'operation': 'loadPaypal',
                                            'errorType': 'ajaxError',
                                            'err': JSON.stringify(error),
                                            'func': 'onApprove',
                                            'errMsg': 'Could not complete address update or redirection to payment page for paypal express checkout'
                                        });
                                     }
                                });
                        },
                        style: {
                            color: 'white',
                            shape: 'pill',
                            label: 'checkout',
                            height: 55,
                            size: 'responsive',
                            layout: 'horizontal',
                            tagline: false
                        }
                    }).render('#paypal-button').then(function () {
                    });
                })          
            
            }
        }
        catch (error) {
            console.log(error);
            dispatch(stopLoading());
            const {applicationState: {currentPage}} = getState();
            logSentry({
                'page': currentPage,
                'feature': 'cartOperations',
                'operation': 'getIdpDetails',
                'errorType': 'ajaxError',
                'err': JSON.stringify(error),
                'func': 'getIdpDetails',
                'api': `${config('LAMBDA_BRAINTREE')}`,
                'errMsg': 'Error:  while getting idp details'
            })
        }
    }
}

const getPaypalSupportedLocale = () => {
    var lang = window.np.clientData.paypalLanguageOverride ? window.np.clientData.paypalLanguageOverride.toLowerCase() : window.locale_details.language.toLowerCase(),
        locale = lang + '_' + window.locale_details.country_code.toUpperCase();
    return locale ? locale : 'en_US'; //eslint-disable-line no-undef
}

const createDataFromPaypalDetails = (paypalData) => {
    const customerData = {
        "firstName": paypalData.firstName,
        "lastName": paypalData.lastName,
        "email": paypalData.email,
        "phone": paypalData.phone ? formatPhoneNumber(paypalData.phone) : ''
    };
    const firstName = (paypalData.shippingAddress.recipientName).split(" ")[0];
    const lastName = (paypalData.shippingAddress.recipientName).split(" ")[1];
    let shippingAddressData = createAddressData(firstName, lastName, customerData, paypalData.shippingAddress);
    let billingAddressData = createAddressData(paypalData.firstName, paypalData.lastName, customerData, paypalData.billingAddress);

    return {customerData,shippingAddressData,billingAddressData};
}

const createAddressData = (firstName, lastName, customerData, addressData) => {
    addressData.postalCode = addressData.postalCode.trim();
    if(window.locale_details.country === 'US' && addressData.postalCode.length === 9){
        const postalCode = addressData.postalCode;
        addressData.postalCode = `${postalCode.slice(0, 5)}-${postalCode.slice(5)}`;
    }
    return {
        "firstName": firstName,
        "lastName": lastName,
        "email": customerData.email,
        "phone": customerData.phone,
        "country": addressData.countryCode || '',
        "city": addressData.city || '',
        "postalCode": addressData.postalCode || '',
        "state": addressData.state || '',
        "streetName" : addressData.line1 || '',
        "additionalStreetInfo" : addressData.line2 || '',
        // below fields have been added because
        // order confirmation mail is blocking emails without below fields
        "additionalAddressInfo": '',
        "title": '',
        "company": '',
        "custom": {
            'fields': {
                'phoneExtension': '',
                'loqateInformation': `{\"addressValidationType\":\"${Address.PAYPAL_VALIDATED}\"}`
            }
        }
    };

}

const updateCustomerAndCartAddressGuest = async ({customerData,shippingAddressData,billingAddressData,nonce, expressCheckoutCookieName, cartId, user, shippingType, dispatch, setShowPaymentError,navigate, triggerMiniCartQuery}) => {
    const customerEmail = customerData.email;
    window.np.identifyUserandDropCookie(customerEmail);
    try{
        getCustomerPreference(customerData, dispatch);
    }
    catch(error){
        console.log(error);
    }
    Promise.all([getAndUpdateCustomer(customerData, dispatch), createGuestCustomer(customerData, dispatch)])
    .then(async (response:any) => {
        if(!response[0].isResolved && !response[1].isResolved) {
            throw { //eslint-disable-line
                message:'Could not create or update customer details'
            }; 
        }
        const customerId = response[0].customerId ? response[0].customerId : response[1].customerId;
        setCookie('np.customerId', customerId);
        window?.np?.enhanceHotjarUserAttributes({"customerId":customerId})
        window.reorder?.fetchInfo(); // fetch reorder info
        await updateInfoAndRedirectToPaymentPage({shippingAddressData,billingAddressData, customerId,customerEmail,nonce, expressCheckoutCookieName, cartId, user, shippingType,dispatch,navigate,triggerMiniCartQuery});
    })
    .catch((error) => {
        if(error.message) {
            console.log(error);
            showPaypalErrorMessage(setShowPaymentError);
            dispatch(stopLoading());
        }
        throw error;
    });
}

const updateCustomerAndCartAddressLoggedIn = async({shippingAddressData,billingAddressData,nonce, expressCheckoutCookieName, user, cartId, shippingType, dispatch,navigate, triggerMiniCartQuery, channelType}) => {
    try {
        const customerId = user.userId;
        const customerEmail = user.email;
        const {data: customer} = await dispatch(customerApi.endpoints.getCustomerById.initiate({ customerId }));
        let shippingResp: any = await addAddress(Address.SHIPPING, customer, shippingAddressData, channelType, dispatch);
        if (shippingResp.modifiedAddressKey) 
            shippingAddressData.key = shippingResp.modifiedAddressKey;

        let billingResp: any = await addAddress(Address.BILLING, customer, billingAddressData, channelType, dispatch);
        if (billingResp.modifiedAddressKey) 
            billingAddressData.key = billingResp.modifiedAddressKey;

        if (shippingResp && billingResp) {
            await updateInfoAndRedirectToPaymentPage({shippingAddressData,billingAddressData, customerId, customerEmail, nonce, expressCheckoutCookieName, cartId, user, shippingType, dispatch,navigate, triggerMiniCartQuery});
        }
    }
    catch(error){
        throw error;
    }
}

const updateInfoAndRedirectToPaymentPage = async({shippingAddressData,billingAddressData, customerId,customerEmail,nonce, expressCheckoutCookieName,cartId, user, shippingType, dispatch, navigate, triggerMiniCartQuery}) => {
    try {
        let resp = await addAddressInCart({shippingAddressData,billingAddressData, customerId, customerEmail,cartId,user,shippingType, dispatch, triggerMiniCartQuery});
        if(resp?.data?.data?.attributes?.id){
            setCookie(expressCheckoutCookieName,nonce, 3*60*60, "/");
            dispatch(stopLoading());
            navigate(`/${config('locale_details').current_sitename}/${i18n.t('checkout_shipping.paymentUrl')}`);
        } else {
            window.location.reload();
        }
    }
    catch(error) {
        throw error;
    }
}

const addAddress = async(addressType, customer, addressData, channelType, dispatch) => {
    try {
        const newAddressData = JSON.parse(JSON.stringify(addressData)); // Making copy so it does not modify parent object
        let updatedCustomerResponse = dispatch(customerActions.addAddress({customer, addressType, channelType, addressData:newAddressData}));
        return updatedCustomerResponse;
    }
    catch(error) {
        throw error;
    }
}

const addAddressInCart = async({shippingAddressData,billingAddressData,customerId, customerEmail, cartId ,user, shippingType, dispatch, triggerMiniCartQuery}) => {
    try {   
            
            const latestCart = await triggerMiniCartQuery(user);
            if(latestCart?.data?.data?.cart?.id) {
            const fakeOrderCookie = getCookie('fake');
            let fakeOrder = 'false';
            if (fakeOrderCookie && fakeOrderCookie !== 'false') fakeOrder = 'true';
            let email = customerEmail;
            if (user.email) email = user.email;

            let actions: any = [
                {
                    'action': 'shippingAddress',
                    'value': shippingAddressData
                },
                {
                    'action': 'billingAddress',
                    'value': billingAddressData
                },
                {
                    'action': 'customerId',
                    'value': customerId
                },
                {
                    'action': 'customField',
                    'value':{
                        'name': 'orderAcquisitionChannel',
                        'value': JSON.stringify({ 'referrer_url': window.locale_details.site_baseurl + '/', 'source': 'website', 'source_crm_agent': false })
                    }
                },
                {
                    'action': 'customField',
                    'value':{
                        'name': 'fakeOrder',
                        'value': fakeOrder
                    }
                },
                {
                    'action': 'customField',
                    'value':{
                        'name': 'orderUserFlow',
                        'value': user.isLoggedIn === 'true' ? 'loggedin' : 'guest'
                    }
                },
                {
                    'action': 'customerEmail',
                    'value': email
                },
                {
                    'action': 'customField',
                    'value':{
                        'name': 'orderType',
                        'value': window?.sessionStorage?.getItem('utm_source') || 'Web'
                    }
                },
            ];

            let shippingTypeValue = 'UPS GROUND'
            if (config('regionCode') === 'CA') shippingTypeValue = 'UPS STANDARD'
            if (shippingType === '') {
                actions.push({
                    'action': 'customField',
                    'value':{
                        'name': 'shippingType',
                        'value': shippingTypeValue
                    }
                });
            }

            let response = await dispatch(newCartApi.endpoints.updateCart.initiate({cartId, actions, isExpressCheckout : true}));
            return response;
        }
    }
    catch (error) {
        throw error;
    }
}

const getDisplayContactPref = (data) => {
    try{
        let preference = data?.subscribe;
        const localPref = getFromLocalStorage('contactPref');
        if (localPref && (localPref.email === data?.emailAddress)) {
            preference = localPref.consent;
        }
            return preference;
        }
    catch(error){
        console.log(error);
    }
  }

const getCustomerPreference = (addressData, dispatch) => {
    return new Promise(async(resolve, reject) => {
        try{
            const email = addressData.email;
            const response = await dispatch(customerGdprAPI.endpoints.getContactPreference.initiate({ email: encodeURIComponent(email)?.trim() }));
            const preference = getDisplayContactPref(response.data?.data);
            if(preference !=='N/A'){
                setInLocalStorage('contactPref', {
                    'email': email,
                    'consent': preference
                });
            }
        }
        catch(error){
            console.log(error);
            reject({ isResolved: false });
        }
    })
}

const getAndUpdateCustomer = (addressData, dispatch) => {
    return new Promise(async(resolve, reject) => {
        try {
            const email = addressData.email;
            const res: any = await dispatch(customerApi.endpoints.getCustomerByEmail.initiate({email} , { forceRefetch: true }));
            if (res?.data?.results?.length > 0) {
                getCustomerResponse = true;
                const customer = res.data?.results[0];
                const customerStatus = customer.custom?.fields?.customerStatus ?? '';
                if (customerStatus === '' || customerStatus === 'GUEST_USER') { // Update Guest customer
                    updateCustomerDetails(addressData, dispatch).then((response: any) => {
                        if (response.isResolved) {
                            resolve({ isResolved: true, customerId: res.data.results[0].id});
                        } else throw 'Unable to update customer details'; //eslint-disable-line
                    }).catch((err) => {
                        reject({ isResolved: false });
                    });
                }
                else{
                    if(customer.isUserDetailsEmpty){
                        const payload = {
                            firstName : addressData.firstName,
                            lastName : addressData.lastName,
                            email : addressData.email
                        };
                        updateCustomerDetails(payload, dispatch).then((response: any) => {
                            if (response.isResolved) {
                                resolve({ isResolved: true, customerId: res.data.results[0].id});
                            } else throw 'Unable to update customer details'; //eslint-disable-line
                        }).catch((err) => {
                            reject({ isResolved: false });
                        });
                    }
                    else resolve({ isResolved: true , customerId: res.data.results[0].id}); // do not update registered or new customer details
                }
            } else resolve({ isResolved: false }); // if no customer information is present
        }
        catch(error) {
            reject({ isResolved: false });
        }
    });
}

const createGuestCustomer = (addressData: any, dispatch) =>{
    return new Promise(async(resolve, reject) => {
        try {
            addressData.customerNumber = await abcService.generateCustomerNumber();
            addressData.locale = config('locale');
            const res: any = await dispatch(customerApi.endpoints.createGuestCustomer.initiate(addressData))
            if (!getCustomerResponse) {
                if(res.error) throw res.error.data.message;
                if (res?.data?.body) resolve({ isResolved: true, customerId: res.data.body.id});
                else reject({ isResolved: false });
            } else resolve({ isResolved: true });
        }
        catch(error: any) {
            if (error.responseJSON?.message[0]?.code === 'DuplicateField') {
                resolve({ isResolved: true });
            } else reject({ isResolved: false });
        }
    });
}

const updateCustomerDetails = (addressData, dispatch) => {
    return new Promise(async(resolve, reject) => {
        try {
            const res: any = await dispatch(customerApi.endpoints.updateCustomer.initiate(addressData));
            if (res?.data?.body?.id) resolve({ isResolved: true, customerId: res.data.body.id });
            else reject({ isResolved: false });
        }
        catch(error) {
            reject({ isResolved: false });
        }
    });
}

const showPaypalErrorMessage = (setShowPaymentError) => {
    setShowPaymentError(true);
}

function createPayloadForIdpDetails(cart: Cart) {
    const cartLineItems = cart?.lineItems;
    const actionsArr: any = [];
    Object.keys(cartLineItems).forEach((lineItemId) => {
        const earlyExpectedArrivalDate = cartLineItems[lineItemId][0]?.data?.idpFromDate;
        const lateExpectedArrivalDate = cartLineItems[lineItemId][0]?.data?.idpToDate;
        if(earlyExpectedArrivalDate && lateExpectedArrivalDate) {
            actionsArr.push({
                earlyExpectedArrivalDate,
                lateExpectedArrivalDate
            })
        }
    })
    return actionsArr;
}

const saveIDPDetails = async (cart, dispatch) => {
    if (config('locale') === 'de-DE'){
        return;
    }
    const payLoad = createPayloadForIdpDetails(cart);
    if(payLoad.length === 0) return;
    return dispatch(cartApi.endpoints.saveLineItemCustomField.initiate({cartId: cart.cartId, actionsArr: payLoad}));
}

const handlePaymentResponse = async (dispatch, data, redirectToOrderConfirmation, setShowPaymentError, paypalPaymentType, paymentMethod,cart) => {
    let _metaData = {
        'event': "payment_method",
        'payment_type': paypalPaymentType,
        'step_name': 'payment_success', // based on type of payment
        'step_no': 2,
        ...addPCCFlag(cart?.hasPCCItem)
      };
      if (data?.isSucessfull && data?.authorizationMessage === "Approved" && data?.orderId && data?.orderId !== 'XX') {
          dispatchGaAndFacebookEvent(_metaData);
          await redirectToOrderConfirmation(data.orderId);
      }else if(data?.orderId && data?.orderId === 'XX'){
        dispatchGaAndFacebookEvent(_metaData);
        await redirectToOrderConfirmation('');
      } else {
            if (paymentMethod === PAYMENT_METHODS.BRAINTREE_CREDITCARD) {
                const metaDataCC = {
                    event: 'cc_decline',
                    label: data.settlementMessage,
                    card_type: data.cardType,
                    ...addPCCFlag(cart?.hasPCCItem)
                  };
                  dispatchGaAndFacebookEvent(metaDataCC);
            }
            
            let _metaDataError = {
              'event': "payment_method",
              'payment_type': paypalPaymentType,
              'step_name': 'payment_fail', // based on type of payment
              'step_no': 2,
              ...addPCCFlag(cart?.hasPCCItem)
          }
          dispatchGaAndFacebookEvent(_metaDataError);
          showPaypalErrorMessage(setShowPaymentError);
      }
      dispatch(stopLoading());
}

const submitNonce = async (dispatch, cartId, redirectToOrderConfirmation, nonce, setShowPaymentError, paypalPaymentType, cart) => {
    dispatch(startLoading());
    await saveIDPDetails(cart, dispatch);
    const countryCode = config('regionCode')?.toLowerCase() ?? 'ie';
    const request_data = {
        'nonce' : nonce,
        'cartId': cartId, 
        'regionCode': countryCode.toUpperCase(),
        'url': window.location.href,
        'expressCheckout': !!getCookie('expressCheckout') 
    };
    try {
        const {data} = await dispatch(brainTreeApi.endpoints.brainTreeCheckout.initiate(request_data))
        await handlePaymentResponse(dispatch, data.data, redirectToOrderConfirmation, setShowPaymentError, paypalPaymentType, null, cart);
    } catch (error) {
        console.log(error)
    }
    
}

const submitNonceNew = async (dispatch, requested_data, redirectToOrderConfirmation, setShowPaymentError, paypalPaymentType, cart, paymentMethod) => {
    dispatch(startLoading());
    await saveIDPDetails(cart, dispatch);
    try {
        let data;
        switch (paymentMethod) {
            case PAYMENT_METHODS.BRAINTREE_CREDITCARD:
                ({data} = await dispatch(brainTreeApiV2Card.endpoints.brainTreeCheckout.initiate(requested_data)));
                break;
        
            case PAYMENT_METHODS.BRAINTREE_APPLE_PAY:
                ({data} = await dispatch(brainTreeApiV2ApplePay.endpoints.brainTreeCheckout.initiate(requested_data)));
                break;
            default:
                break;
        }
        await handlePaymentResponse(dispatch, data, redirectToOrderConfirmation, setShowPaymentError, paypalPaymentType, paymentMethod,cart);
        return data;
    } catch (error) {
        console.log(error)
    }
}

export default { //eslint-disable-line
    createClientInstance,
    loadPaypal,
    loadPayPalInstance,
    submitNonce,
    submitNonceNew,
    addAddress,
    updateCustomerAndCartAddressLoggedIn,
    updateCustomerAndCartAddressGuest,
    paypalPayLaterIsEligible
};
